/**
 * view-manager.js — the single source of truth for HOUND's view system.
 *
 * Architectural goal: replace hundreds of inline-style mutations with
 * declarative CSS rules driven by ONE attribute on <html>: data-view.
 *
 *   <html data-view="quest"  data-product="">             → quest panel visible
 *   <html data-view="merchants-den" data-product="HpCkEB"> → shop + Seraph open
 *   <html data-view="idle"   data-product="">             → parallax visible, no UI
 *   <html data-view="title"  data-product="">             → title screen (initial)
 *
 * Valid view names:  title | idle | merchants-den | guide | quest | world-map
 *
 * Architecture doc: dev/hound-website/docs/architecture-refactor-plan.md
 *
 *
 * DESIGN (post-consolidation, Phase 5.5):
 *
 *   ViewManager is SYNCHRONOUS. setView() does exactly three things,
 *   atomically, with no internal timers or queues:
 *     1. fire 'will-change'      (listeners clean up the outgoing view)
 *     2. set <html data-view>    (CSS reacts → sections show/hide)
 *     3. fire 'did-change'       (listeners set up the incoming view)
 *
 *   It does NOT run its own transition clock. The CSS transition duration
 *   lives in CSS (--view-transition-duration); whoever needs to wait for a
 *   visual settle reads it themselves. State-manager owns transition
 *   ORCHESTRATION (video preload, scroll locks, the actual work) and its
 *   own isTransitioning flag + queue for rapid clicks. Keeping a second
 *   async transition clock here created two competing state machines that
 *   raced (the "setView QUEUED" churn). One clock, one owner.
 *
 *   ViewManager is the SINGLE caller surface: state-manager calls setView()
 *   exactly once per navigation (at the start). There is no longer a
 *   legacy 'navigate'/'viewChange' event mirror here — that produced a
 *   double-call. The dispatcher of 'navigate' (title buttons, char clicks)
 *   is heard by state-manager, which is the one place that calls setView.
 */

(function () {
  'use strict';

  // 'title' and 'main-menu' both denote the title/menu screen. The page
  // loads with data-view="title" (server-rendered); the in-app header's
  // "Main Menu" button navigates to 'main-menu'. They are the same view;
  // both are valid so setView() from either source succeeds and updates
  // data-view (which is what makes the CSS-driven views — guide/quest —
  // hide when you return to the menu). Phase 7 will unify the name.
  const VALID_VIEWS = ['title', 'main-menu', 'idle', 'merchants-den', 'guide', 'quest', 'world-map'];
  const VERSION = '2026-05-28T11:00Z phase-5.5b-guide-fixes';

  (window.HDLOG||function(){})(
    '%c[HD] view-manager loaded — build ' + VERSION,
    'color:#7aff7a;font-weight:bold'
  );

  class ViewManager {
    constructor() {
      this._view    = document.documentElement.getAttribute('data-view') || 'title';
      this._product = document.documentElement.getAttribute('data-product') || '';
      this._listeners = { 'will-change': [], 'did-change': [] };
    }

    /** @returns {string} current view name */
    getView() { return this._view; }

    /** @returns {string} active product key inside merchants-den, '' otherwise */
    getProduct() { return this._product; }

    /**
     * Change the active view. THE ONLY mutation entry point for views.
     * Synchronous: by the time this returns, <html data-view> is set and
     * both lifecycle events have fired.
     *
     * @param {string} viewName  one of VALID_VIEWS
     * @param {object} [opts]
     * @param {string}  [opts.reason='']  diagnostic label
     * @returns {boolean} true if the view changed, false if it was a no-op
     */
    setView(viewName, opts = {}) {
      if (!VALID_VIEWS.includes(viewName)) {
        console.error('[HD] view-manager: invalid view "' + viewName + '". Valid:', VALID_VIEWS);
        return false;
      }
      if (viewName === this._view) return false;

      const from = this._view;
      (window.HDLOG||function(){})(
        '[HD] view → ' + viewName + ' (from ' + from + ')' +
        (opts.reason ? ' | ' + opts.reason : '')
      );

      // The brothers' spotlight belongs to the first street introduction only.
      // Once a destination has been visited, unselecting returns to a bright street.
      // Keep this for this page visit; a fresh load can introduce the brothers again.
      if (!['title', 'main-menu', 'idle'].includes(viewName)) {
        document.documentElement.setAttribute('data-release-intro-complete', 'true');
      }

      this._fire('will-change', { from: from, to: viewName });
      this._view = viewName;
      document.documentElement.setAttribute('data-view', viewName);
      this._fire('did-change', { from: from, to: viewName });
      return true;
    }

    /**
     * Set the active product within merchants-den (or '' for none).
     * Just an attribute swap — section CSS handles the fade via data-product.
     * @param {string} productKey  the active product's id (the same value the
     *   armoury buttons carry as data-product-id), or '' when nothing is
     *   equipped. Written by the product selector at its visible commit.
     */
    setProduct(productKey) {
      productKey = productKey || '';
      if (productKey === this._product) return;
      (window.HDLOG||function(){})('[HD] product → "' + productKey + '" (from "' + this._product + '")');
      this._product = productKey;
      document.documentElement.setAttribute('data-product', productKey);
    }

    /**
     * Subscribe to a view lifecycle event.
     * @param {'will-change'|'did-change'} event
     * @param {(detail:{from:string,to:string})=>void} fn
     * @returns {()=>void} unsubscribe
     */
    on(event, fn) {
      if (!this._listeners[event]) return function () {};
      this._listeners[event].push(fn);
      const self = this;
      return function () {
        self._listeners[event] = self._listeners[event].filter(function (f) { return f !== fn; });
      };
    }

    _fire(event, detail) {
      (this._listeners[event] || []).forEach(function (fn) {
        try { fn(detail); }
        catch (e) { console.error('[HD] view-manager listener error:', e); }
      });
    }
  }

  window.viewManager = new ViewManager();
})();
